import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Code,
  Container,
  Grid,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Switch,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { getImport, getMappingDetails, retryFailedOptivise } from "../api";
import { Link } from "wouter";
import { IconRefresh } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

function formatFailureType(type) {
  if (type === "optimisationFailed") return "Optimisation failed";
  if (!type) return "Issue";
  return type;
}

function MappingErrorTooltip({ failures }) {
  return failures.map((failure, index) => (
    <Box key={index} mb="sm">
      <Text fz="sm" fw={600}>
        {formatFailureType(failure.type)}{" "}
        {(failure.source || failure.dest) && (
          <Code>
            {failure.source} {"\u27F6"} {failure.dest}
          </Code>
        )}
      </Text>
      <Text fz="sm" c="dimmed">
        {failure.message}
      </Text>
      {failure.attemptedValue && (
        <>
          <Text fz="sm">
            Attempted Value: <Code>{failure.attemptedValue}</Code>
          </Text>
        </>
      )}
    </Box>
  ));
}

function MappingFailure({ failures }) {
  if (!failures || failures.length === 0) return " - ";
  let color = "yellow";
  if (failures.some((f) => f.severity === "error")) color = "red";
  return (
    <Tooltip
      maw="40%"
      multiline
      label={<MappingErrorTooltip failures={failures} />}
      position="top"
      withArrow
    >
      <Badge color={color}>{failures.length}</Badge>
    </Tooltip>
  );
}

function mappingStatus(mapping) {
  return (mapping?.process?.status ?? "").toLowerCase();
}

function MappingDetails({ params }) {
  const [importData, setImportData] = useState(null);
  const [showAllAttributes, setShowAllAttributes] = useState(false);
  const [showOnlyIssues, setShowOnlyIssues] = useState(true);
  const [mapping, setMapping] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    getMappingDetails(params.mappingId).then((data) => {
      setMapping(data);
    });
  }, [params.mappingId]);

  useEffect(() => {
    getImport(params.importId).then((data) => {
      setImportData(data);
    });
  }, [params.importId]);

  const issuesByProtoId = useMemo(() => {
    const map = new Map();
    for (const issue of mapping?.issues ?? []) {
      if (issue?.protoId) map.set(issue.protoId, issue.failures ?? []);
    }
    return map;
  }, [mapping]);

  if (!importData || !mapping) {
    return (
      <Container size="xl">
        <Center>
          <Loader />
        </Center>
      </Container>
    );
  }

  const displayColumns = showAllAttributes
    ? importData.columns
    : importData.columns.slice(0, 4);

  const keys = importData.columns?.map((p) => p.key);
  if (!keys) {
    return <div>No keys!</div>;
  }

  const issueCount = issuesByProtoId.size;
  const totalCount = importData.productVariantProtos.length;
  let errorCount = 0;
  let warningCount = 0;
  for (const failures of issuesByProtoId.values()) {
    if (failures.some((f) => f.severity === "error")) errorCount += 1;
    else if (failures.length > 0) warningCount += 1;
  }
  const itemsToDisplay = (
    showOnlyIssues && issueCount > 0
      ? importData.productVariantProtos.filter((p) => issuesByProtoId.has(p.id))
      : importData.productVariantProtos
  )
    .slice()
    .sort((a, b) => (a.rowIndex ?? 0) - (b.rowIndex ?? 0));

  const status = mappingStatus(mapping);
  const isProcessing = status === "processing" || status === "queued";
  const canRetry = errorCount > 0 && !isProcessing;

  const retryFailed = () => {
    setIsRetrying(true);
    retryFailedOptivise(params.mappingId)
      .then((updated) => {
        notifications.show({
          title: "Retry started",
          message:
            "Failed products are being optimised again. Already-successful products are left as they are.",
          autoClose: 20_000,
        });
        setMapping((prev) => ({
          ...prev,
          process: updated ?? { ...prev.process, status: "processing" },
        }));
      })
      .finally(() => setIsRetrying(false));
  };

  return (
    <Container size="xl">
      <Anchor onClick={() => window.history.back()} c="gray">
        ← Back
      </Anchor>

      {mapping?.process?.abortReason && (
        <Alert color="red" title="Mapping Aborted" mb="md">
          Your mapping was aborted due to the following reason:{" "}
          {mapping.process.abortReason}
        </Alert>
      )}

      {(errorCount > 0 || warningCount > 0) && (
        <Alert color={errorCount > 0 ? "red" : "yellow"} variant="light" my="md">
          <Group justify="space-between" align="center">
            <Stack gap={4}>
              {errorCount > 0 && (
                <Text>
                  {errorCount} of {totalCount} products failed. Hover a red
                  badge to see why.
                </Text>
              )}
              {warningCount > 0 && (
                <Text>
                  {warningCount} of {totalCount} products have a warning. Hover
                  a badge to see why.
                </Text>
              )}
            </Stack>
            {errorCount > 0 && (
              <Button
                size="xs"
                variant="default"
                leftSection={<IconRefresh size={14} />}
                loading={isRetrying}
                disabled={!canRetry}
                onClick={retryFailed}
              >
                Retry failed products
              </Button>
            )}
          </Group>
        </Alert>
      )}

      {issueCount === 0 && !isProcessing && (
        <Alert color="gray" variant="light" my="md">
          No per-product errors were found. Showing all {totalCount} products.
        </Alert>
      )}

      {isProcessing && (
        <Alert color="blue" variant="light" my="md" icon={<Loader size={16} />}>
          Optimisation is still running. Failed products will show here as they
          are recorded.
        </Alert>
      )}

      <Grid>
        <Grid.Col span={12}>
          <Group mb="sm" justify="space-between">
            <Link to={`/imports/${importData.id}`}>
              <Text fz="sm" fw={600} mb="sm">
                {importData.originalName}
              </Text>
            </Link>

            <Text fz="sm" c="dimmed">
              {showOnlyIssues && issueCount > 0
                ? `${issueCount} of ${totalCount} SKU with issues`
                : `${totalCount} SKU`}
            </Text>

            <Group>
              <Switch
                checked={showOnlyIssues}
                disabled={issueCount === 0}
                label="Show only issues"
                onChange={() => setShowOnlyIssues((prev) => !prev)}
              />
              <Switch
                checked={showAllAttributes}
                label="Show all attributes"
                onChange={() => setShowAllAttributes((prev) => !prev)}
              />
            </Group>
          </Group>
          <ScrollArea type="always" h="80dvh" w="100%">
            <Table stickyHeader fz="xs" withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={30}>Row</Table.Th>
                  <Table.Th w={30}>Issues</Table.Th>
                  <Table.Th w={40} style={{ borderRight: "1px solid #ccc" }}>
                    Attributes
                  </Table.Th>
                  {displayColumns.map((column) => (
                    <Table.Th key={column.key} style={{ whiteSpace: "nowrap" }}>
                      {column.key}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {itemsToDisplay.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={3 + displayColumns.length}>
                      <Text ta="center" c="dimmed" py="xl">
                        No products match this filter.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {itemsToDisplay.map((productVariantProto, index) => {
                  const failures = issuesByProtoId.get(productVariantProto.id);
                  const rowNumber =
                    typeof productVariantProto.rowIndex === "number"
                      ? productVariantProto.rowIndex + 1
                      : index + 1;
                  return (
                    <Table.Tr key={productVariantProto.id}>
                      <Table.Td ta="right">{rowNumber}</Table.Td>
                      <Table.Td ta="right">
                        <MappingFailure failures={failures} />
                      </Table.Td>
                      <Table.Th
                        ta="right"
                        style={{ borderRight: "1px solid #ccc" }}
                      >
                        {keys.length}
                      </Table.Th>
                      {displayColumns.map(({ key }) => (
                        <Table.Td
                          style={{ whiteSpace: "nowrap" }}
                          key={`${productVariantProto.id}-${key}`}
                        >
                          {productVariantProto.fieldValues[key]}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Grid.Col>
      </Grid>
    </Container>
  );
}

export default MappingDetails;
